import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ServiceUser } from '@sr/types';
import { env } from 'customer-portal-helpers/env';
import { getApiUrl } from '#lib/utils/environment';
import { WithCustomerPortalApi } from '../customerPortalApi';

const BASE_PATH_USER_ADMIN = env(
  'PORTAL_RECYCLING_USER_ADMIN_BASE_PATH',
  '/stenarecycling/usermanagement',
);

const userAdminApiUrl = `${getApiUrl()}${BASE_PATH_USER_ADMIN}`;

const sortByName = (users: ServiceUser[]): ServiceUser[] => {
  return [...users].sort((a, b) => {
    if (!a.fullName && !b.fullName) return 0;
    if (!a.fullName) return 1;
    if (!b.fullName) return -1;

    return a.fullName.localeCompare(b.fullName, 'sv');
  });
};

type Props = {
  userId: string | undefined;
  requireManageSubscriptions?: boolean;
  sortByName?: boolean;
};

export const useUsersForCurrentUser = WithCustomerPortalApi<
  ServiceUser[],
  UseQueryResult<ServiceUser[]>,
  Props
>(
  (
    fetchFn,
    { userId, requireManageSubscriptions = false, sortByName: shouldSortByName = true },
  ) => {
    const url = new URL(`${userAdminApiUrl}/user/${encodeURIComponent(userId || '')}/users`);

    if (requireManageSubscriptions) {
      url.searchParams.set('requireManageSubscriptions', 'true');
    }

    return useQuery({
      queryKey: ['usersForCurrentUser', userId, requireManageSubscriptions],
      enabled: !!userId,
      select: shouldSortByName ? sortByName : undefined,
      queryFn: ({ signal }) =>
        fetchFn({
          url,
          options: {
            method: 'GET',
            signal,
          },
        }),
    });
  },
  { hookName: 'useUsersForCurrentUser' },
);
